import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { AppModule } from "../app.module";
import {  ServiceProviderClient} from '../models/client';
import { ServiceProviderService } from './service-provider.service';
import { ServiceProviderEdit } from "./service-provider-edit/service-provider-edit";
import { MapServiceProviders } from "../map/map-service-providers/map-service-providers";
import { NotificationService } from '../shared/notification.service';
import { Subscription } from 'rxjs';
import { IncidentService } from '../shared/incident.service';

@Component({
  selector: 'app-service-provider',
  imports: [AppModule, ServiceProviderEdit, MapServiceProviders],
  templateUrl: './service-provider.html',
  styleUrl: './service-provider.less'
})
export class ServiceProvider implements OnInit, OnDestroy {

  serviceProviders = signal<ServiceProviderClient[]>([]);
  isModalVisible = signal(false);
  selectedSP = signal<ServiceProviderClient | null>(null);
  isLoading = signal<boolean>(false);

  private serviceProviderStatusUpdateSubscription: Subscription;

  spSearchTerm = signal<string>('');
  showOnlyWithLocation = signal<boolean>(false);

  filteredSPs = computed(() => {
    const showOnlyWithLocation = this.showOnlyWithLocation();
    const term = this.spSearchTerm().toLowerCase();
    const spList = this.serviceProviders();
    let spFiltered = spList;
    if (showOnlyWithLocation) {
      spFiltered = spFiltered.filter(sp => 
        sp.latitude !== null && sp.longitude !== null
      );
    }
    if (term && term.trim() !== '' && term.length >= 2)
      return spFiltered.filter(sp => 
        sp.clientName.toLowerCase().includes(term) ||
        (sp.clientGroupName && sp.clientGroupName.toLowerCase().includes(term)) ||
        sp.clientTel.toLowerCase().includes(term) ||
        (sp.addressCity && sp.addressCity.toLowerCase().includes(term))
    );
    return spFiltered;

  })

  sortByDtLastModified = computed(() => {
    return (a: ServiceProviderClient, b: ServiceProviderClient) => {
      const dateA = a.dtLastModified ? new Date(a.dtLastModified).getTime() : 0;
      const dateB = b.dtLastModified ? new Date(b.dtLastModified).getTime() : 0;
      return dateB - dateA;
    };
  });

  constructor(private spService: ServiceProviderService, private notification: NotificationService, private incidentService: IncidentService) {}

  ngOnInit() {
    this.loadServiceProviders();
    this.serviceProviderStatusUpdateSubscription = this.incidentService.$serviceProviderStatusUpdate.subscribe((statusUpdate) => {
      const provider = this.serviceProviders().find(sp => sp.clientId === statusUpdate.clientId);
      //find the userId
      if (provider) {
        const bdtech = provider.technicians?.find(bt => bt.driverId === statusUpdate.userId);
        if (bdtech) {
          bdtech.statusId = statusUpdate.serviceProviderStatusId;
          //this.notification.showInfo(`Service Provider Technician ${bdtech.name} is now ${statusUpdate.serviceProviderStatusId === 1 ? 'Available' : 'Unavailable'}.`);
          //find user and update status
          this.serviceProviders.set([...this.serviceProviders()]); //trigger update
        }
      }
    });

  }

  ngOnDestroy() {
    if (this.serviceProviderStatusUpdateSubscription) {
      this.serviceProviderStatusUpdateSubscription.unsubscribe();
    }
  }

  private loadServiceProviders() {
    this.isLoading.set(true);
    this.spService.getServiceProviders().subscribe(
      {next: (data: ServiceProviderClient[]) => {
          this.serviceProviders.set(data);
          this.notification.showSuccess('Service Providers loaded successfully.');
        },
       error: (error) => {
          this.notification.showError('Error loading Service Providers: ' + error.message);
        },
        complete: () => {
          this.isLoading.set(false);
        }
      }
    );      
  }

  public viewDetails(client: ServiceProviderClient) {
    this.selectedSP.set(client);
    this.isModalVisible.set(true);
  }

  public onProviderSaved(savedProvider: ServiceProviderClient) {
      const existingIndex = this.serviceProviders().findIndex(
    p => p.clientId === savedProvider.clientId
    );
    
    if (existingIndex === -1) {
      // New provider - add to list
      this.serviceProviders.update(providers => [...providers, savedProvider]);
    } else {
      // Existing provider - update in list
      this.serviceProviders.update(providers =>
        providers.map(p => p.clientId === savedProvider.clientId ? savedProvider : p)
      );
    }
  }

  public openNewProviderModal() {
    this.selectedSP.set(null);
    this.isModalVisible.set(true);

  }

  public hasLocation(provider: ServiceProviderClient): boolean {
    return provider.latitude !== null || provider.longitude !== null;
  }

  public removeSPConnection(spId: number): void {
    if (confirm('Are you sure you want to remove this Service Provider connection?')) {
      this.spService.removeSPConnection(spId).subscribe({
        next: () => {
          this.serviceProviders.update(providers => providers.filter(p => p.clientId !== spId));
          this.notification.showSuccess('Service Provider connection removed successfully.');
        },
        error: (error) => {
          this.notification.showError('Error removing Service Provider connection: ' + error.message);
        }
      });
    }
  }

  public requestStatusUpdate(sp: ServiceProviderClient) {
    return this.spService.requestTechnicianStatusUpdate(sp.clientId).subscribe({
      next: (dt: Date) => {
        this.notification.showSuccess('Status update requested successfully.');
        // Update the last request time for this service provider
        this.filteredSPs().forEach(provider => {
          if (provider.clientId === sp.clientId) {
            provider.dtLastStatusRequest = dt;
          }
        });
        this.serviceProviders.set([...this.serviceProviders()]); //trigger update
      },
      error: (error) => {
        this.notification.handleError(error, 'Error requesting status update');
      }
    });
  }

  public isStatusUpdateRequestAvailable(lastUpdateRequest: Date | null | undefined): boolean {
    if (!lastUpdateRequest) {
      return true; // No request made yet, so it's available
    }
    const now = new Date();
    const diffInMinutes = (now.getTime() - new Date(lastUpdateRequest + 'Z').getTime()) / (1000 * 60);
    return diffInMinutes >= 30; // Allow new request if it's been 30 minutes or more
  }
}
